/* global toastr */

import axios from 'axios';
import { parse, stringify } from 'qs';
import crud from './helpers/crud';
import download, { downloadToBrowser } from './helpers/download';
import { updateCanEditStatus } from './faxes';
import { rowSelected } from './inboxTableActions';
import posthog from 'posthog-js';
import { captureAnalytics } from './preload';
import { hasFeatureInLocalStorage, INBOX_BACKGROUND_REFRESH_FEATURE } from '../global/featureFlags';

// Per-org gate for the whole background-refresh behavior. Checked at every
// producer site (the consumer, RecordsHOC, only acts on nonce bumps, which
// cannot happen while this is off). Off = legacy behavior: components fall back
// to page reloads / foreground refetches, so large orgs whose inbox queries run
// 5-8s can be opted in for prod testing and backed out per-org if it's a bad
// experience.
const inboxBackgroundRefreshEnabled = () => (
  hasFeatureInLocalStorage(INBOX_BACKGROUND_REFRESH_FEATURE)
);

// Signals the mounted inbox (RecordsHOC) to silently refetch its current query in
// the background after an action changes which rows belong in the list or what
// their status columns show (archive, notify, send-to-chart). No websockets: the
// HOC watches this nonce in the records reducer and refetches over plain HTTP.
// No-op while the org's inbox_background_refresh feature is off.
const requestInboxTableRefresh = (reason = 'record_action') => (dispatch) => {
  if (!inboxBackgroundRefreshEnabled()) return;
  dispatch({
    type: 'REQUEST_INBOX_TABLE_REFRESH',
    payload: { reason },
  });
};

// Chart uploads are queued and processed by a background job, so the row's chart
// status settles some seconds after the API call succeeds. Refresh once more after
// this delay so the settled status appears without a manual reload.
const CHART_UPLOAD_SETTLE_REFRESH_MS = 12000;

// Background ("silent") inbox refetches reuse the same FINISHED_* action type as a
// user-initiated fetch so table data/headers update in place, but swap begin/fail
// for types no reducer handles: no isLoading spinner, no error state on failure.
const inboxQueryDispatch = (dispatchTypes, { silent = false } = {}) => (silent
  ? { ...dispatchTypes, begin: 'BEGIN_SILENT_INBOX_REFRESH', fail: 'FAILED_SILENT_INBOX_REFRESH' }
  : dispatchTypes);

// Background refresh after a send-to-chart / create-chart: signals RecordsHOC to
// refetch silently (no spinner, no history entry); a second refresh after the
// settle delay picks up the final status once the queued upload job completes.
// No-op when the feature is off — the chart modals then handle the refetch
// themselves via refreshCurrentRecords' legacy branch, as they did before the
// background-refresh callbacks were added to the upload actions.
//
// This is the single chart-upload refresh policy: both producer routes — the
// upload actions' success callbacks and the modals' refreshCurrentRecords —
// converge here, and RecordsHOC's debounce collapses the duplicate signals into
// one refetch. New chart actions should wire a success callback to this helper;
// the redundant modal route (and the legacy branch it exists for) goes away
// with the inbox_background_refresh flag.
//
// ENG-3009: `foregroundFallback` exists because the assumption in the paragraph above
// — "the chart modals then handle the refetch themselves" — does not hold for the
// schema-driven DynamicEhrDrawer. The legacy modals import refreshCurrentRecords and
// call it directly; the drawer only awaits createChartUpload and closes. So with the
// flag off the drawer had no refresh route on ANY page, and the row it just queued an
// upload for stayed stale until a manual reload. Only createChartUpload opts in: the
// legacy actions keep the plain no-op so their modals remain the single refresh caller
// and we do not fire two refetches (and two history entries) per submission.
const chartUploadBackgroundRefresh = ({ foregroundFallback = false } = {}) => (dispatch) => {
  if (!inboxBackgroundRefreshEnabled()) {
    return foregroundFallback ? dispatch(refreshCurrentRecords()) : undefined;
  }
  dispatch(requestInboxTableRefresh('chart_upload'));
  setTimeout(() => {
    dispatch(requestInboxTableRefresh('chart_upload_settled'));
  }, CHART_UPLOAD_SETTLE_REFRESH_MS);
  return undefined;
};

const referralStatusCountsParseOptions = {
  ignoreQueryPrefix: true,
};

const referralStatusCountsStringifyOptions = {
  addQueryPrefix: true,
  arrayFormat: 'brackets',
  encode: true,
  sort: (a, b) => a.localeCompare(b),
};

const buildReferralStatusCountsQuery = (query = '') => {
  const params = parse(query || '', referralStatusCountsParseOptions);
  delete params.referral_statuses;
  delete params.referrals;
  delete params.category;
  delete params.page;
  delete params.page_size;

  return stringify(params, referralStatusCountsStringifyOptions);
};

const buildReferralStatusCountsUrl = (query = '') => {
  const normalizedQuery = buildReferralStatusCountsQuery(query);
  const normalizedSuffix = normalizedQuery && normalizedQuery !== '?' ? normalizedQuery.replace('?', '&') : '';

  return `/api/v1/receivers/referral_status_counts?referrals=true&category=Referral${normalizedSuffix}`;
};

const getUploadedRecordsByPage = (page, pageSize = 10, query = '') => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_UPLOADED_RECORDS',
      fail: 'FAILED_GET_UPLOADED_RECORDS',
      end: 'FINISH_GET_UPLOADED_RECORDS',
    },
    method: 'GET',
    url: `/api/v1/records?page=${page}&page_size=${pageSize}${query}`,
  })
);

const getUploadedByQuery = (query, signal, options = {}) => (
  crud({
    dispatch: inboxQueryDispatch({
      begin: 'BEGIN_GET_UPLOADED_RECORDS',
      fail: 'FAILED_GET_UPLOADED_RECORDS',
      end: 'FINISH_GET_UPLOADED_RECORDS',
    }, options),
    method: 'GET',
    url: `/api/v1/records${query}`,
    // crud() pushes `query` onto browser history; background refreshes must not
    // add history entries.
    query: options.silent ? undefined : query,
    signal,
  })
);

const duplicateRecord = fileToken => (
  crud({
    dispatch: {
      begin: 'BEGIN_DUPLICATE_RECORD',
      fail: 'FAILED_DUPLICATE_RECORD',
      end: 'FINISH_DUPLICATE_RECORD',
    },
    method: 'POST',
    url: `/api/v1/records/${fileToken}/clone`,
  })
);

/** Gets a record by ID
 * Takes in an additional param because the BE now saves an original copy
 * of a record in addition to an edited copy. When the user is accessing a record
 * from the sent inbox, we want to return the unedited copy of the record back
 * to them.
 *
 * @param  {String} id      the record ID (file_token)
 * @param  {Boolean|Object} sentOrOptions - either a boolean for `sent` flag (backward compat)
 *                          or an options object { signal, sent } for AbortController support
 * @param  {Boolean} ext    whether the record is being requested from the Chrome Extension
 * @param  {Boolean} archive whether to fetch from archive
 */
const getRecordById = (id, sentOrOptions = false, ext = false, archive = false) => {
  // Support backward compatibility: second param can be boolean (sent) or object (options)
  let sent = false;
  let options = {};

  if (sentOrOptions && typeof sentOrOptions === 'object') {
    options = sentOrOptions;
    sent = options.sent || false;
  } else {
    sent = sentOrOptions;
  }

  return crud({
    dispatch: {
      begin: 'BEGIN_GET_RECORD_DETAILS',
      fail: 'FAILED_GET_RECORD_DETAILS',
      end: 'FINISHED_GET_RECORD_DETAILS',
    },
    method: 'GET',
    url: `/api/v1/records/${id}?sent=${sent}&ext=${ext}&archive=${archive}`,
    signal: options.signal,
  });
};

const getSentRecordsByPage = page => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_SENT_RECORDS',
      fail: 'FAILED_GET_SENT_RECORDS',
      end: 'FINISHED_GET_SENT_RECORDS',
    },
    method: 'GET',
    url: `/api/v1/records/sent?page=${page}`,
  })
);

// ENG-3009: gained the silent/abortable signature so the Sent page can join
// SILENT_REFRESH_MODES and actually show the chart_creation chips it now serializes.
// Mirrors getUploadedByQuery — without the `query` guard a background refresh would
// push a browser history entry on every poll.
const getSentByQuery = (query, signal, options = {}) => (
  crud({
    dispatch: inboxQueryDispatch({
      begin: 'BEGIN_GET_SENT_RECORDS',
      fail: 'FAILED_GET_SENT_RECORDS',
      end: 'FINISHED_GET_SENT_RECORDS',
    }, options),
    method: 'GET',
    url: `/api/v1/records/sent${query}`,
    query: options.silent ? undefined : query,
    signal,
  })
);

const getSentRecordBySendToken = (sendToken, options = {}) => {
  const patientParam = options.patient ? '?patient=true' : '';
  return crud({
    dispatch: {
      begin: 'BEGIN_GET_RECORD_DETAILS',
      fail: 'FAILED_GET_RECORD_DETAILS',
      end: 'FINISHED_GET_RECORD_DETAILS',
    },
    method: 'GET',
    url: `/api/v1/records/sent/${sendToken}${patientParam}`,
    signal: options.signal,
  });
};

const getReceivedRecordsByPage = (page, pageSize = 10, includeSignedReplies = '') => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_RECEIVED_RECORDS',
      fail: 'FAILED_GET_RECEIVED_RECORDS',
      end: 'FINISHED_GET_RECEIVED_RECORDS',
    },
    method: 'GET',
    url: `/api/v1/receivers?page=${page}&page_size=${pageSize}${includeSignedReplies}`,
    data: includeSignedReplies,
  })
);

const getReferralsByPage = (page, pageSize = 10) => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_RECEIVED_RECORDS',
      fail: 'FAILED_GET_RECEIVED_RECORDS',
      end: 'FINISHED_GET_RECEIVED_RECORDS',
    },
    method: 'GET',
    url: `/api/v1/receivers?page=${page}&page_size=${pageSize}&referrals=true&category=Referral`,
  })
);

const getReferralDocumentStatus = (fileToken, sendToken, parentType) => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_REFERRAL_STATUS',
      fail: 'FAILED_GET_REFERRAL_STATUS',
      end: 'FINISHED_GET_REFERRAL_STATUS',
    },
    method: 'GET',
    url: `/api/v1/records/${fileToken}/referred_document_statuses/${sendToken}?parent_type=${parentType}`,
  })
);

const getAssignedRecordsByPage = (page, pageSize = 10, includeSignedReplies = '') => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_ASSIGNED_RECORDS',
      fail: 'FAILED_GET_ASSIGNED_RECORDS',
      end: 'FINISHED_GET_ASSIGNED_RECORDS',
    },
    method: 'GET',
    url: `/api/v1/assigned_documents?page=${page}&page_size=${pageSize}${includeSignedReplies}`,
    data: includeSignedReplies,
  })
);

const updateAssignedRecord = (assignedDocId, statusId, assignee) => (
  crud({
    dispatch: {
      begin: 'BEGIN_UPDATE_ASSIGNED_RECORD_STATUS',
      fail: 'FAILED_UPDATE_ASSIGNED_RECORD_STATUS',
      end: 'FINISHED_UPDATE_ASSIGNED_RECORD_STATUS',
    },
    method: 'POST',
    url: `/api/v1/assigned_documents/${assignedDocId}/assigned_document_statuses`,
    data: {
      assigned_document_status: {
        document_status_id: statusId,
        assignee,
      },
    },
  })
);

const updateReferralDueDate = (recordId, referralStatusSlug, dueDate) => (
  crud({
    dispatch: {
      begin: 'BEGIN_UPDATE_REFERRAL_DUE_DATE',
      fail: 'FAILED_UPDATE_REFERRAL_DUE_DATE',
      end: 'FINISHED_UPDATE_REFERRAL_DUE_DATE',
    },
    method: 'PATCH',
    url: `/api/v1/records/${recordId}/referred_document_statuses/${referralStatusSlug}`,
    data: {
      referred_document_status: { due_date: dueDate },
      update_type: 'due_date',
    },
  })
);

const updateReferral = (
  recordId,
  sendToken,
  status,
  parentType,
  preserveDueDate = false,
  referralNote = undefined,
) => (
  crud({
    dispatch: {
      begin: 'BEGIN_UPDATE_REFERRAL_STATUS',
      fail: 'FAILED_UPDATE_REFERRAL_STATUS',
      end: 'FINISHED_UPDATE_REFERRAL_STATUS',
    },
    method: 'POST',
    url: `/api/v1/records/${recordId}/referred_document_statuses`,
    data: {
      referred_document_status: referralNote === undefined
        ? status
        : { ...status, referral_note: referralNote },
      parent_type: parentType,
      send_token: sendToken,
      referral_status_id: status.referral_status_id,
      preserve_due_date: preserveDueDate,
    },
  })
);

const assignDocument = assignDoc => (
  crud({
    dispatch: {
      begin: 'BEGIN_CREATE_ASSIGNED_RECORD',
      fail: 'FAILED_CREATE_ASSIGNED_RECORD',
      end: 'FINISHED_CREATE_ASSIGNED_RECORD',
    },
    method: 'POST',
    url: '/api/v1/assigned_documents/',
    data: {
      assigned_document: assignDoc,
    },
    onSuccess: () => {
      if (process.env.NODE_ENV !== 'development' && posthog) {
        posthog.capture('create_assigned_record', {
          sendToken: assignDoc.send_token,
          note: assignDoc.note,
          assigneeId: assignDoc.assignee_id,
          parentType: assignDoc.parent_type,
          documentStatusId: assignDoc.document_status_id,
          forceAssign: assignDoc.force_assign,
        });
      }
    },
  })
);

const markDocumentAsCompleted = assignedDocumentId => (
  crud({
    dispatch: {
      begin: 'BEGIN_UPDATE_ASSIGNED_RECORD',
      fail: 'FAILED_UPDATE_ASSIGNED_RECORD',
      end: 'FINISHED_UPDATE_ASSIGNED_RECORD',
    },
    method: 'PATCH',
    url: `/api/v1/assigned_documents/${assignedDocumentId}`,
    data: {
      assigned_document: { completed: true },
    },
  })
);

const assignBackAndComplete = assignedDocumentId => (
  crud({
    dispatch: {
      begin: 'BEGIN_ASSIGN_BACK_AND_COMPLETE_ASSIGNED_RECORD',
      fail: 'FAILED_ASSIGN_BACK_AND_COMPLETE_ASSIGNED_RECORD',
      end: 'FINISHED_ASSIGN_BACK_AND_COMPLETE_ASSIGNED_RECORD',
    },
    method: 'POST',
    url: `/api/v1/assigned_documents/${assignedDocumentId}/assign_back_and_complete`,
    onSuccess: (response) => {
      if (response.data.created_return_assignment) {
        const analytics = response.data.assignment_analytics;
        captureAnalytics('create_assigned_record', {
          sendToken: analytics.send_token,
          note: undefined,
          assigneeId: analytics.assignee_id,
          parentType: analytics.parent_type,
          documentStatusId: analytics.document_status_id,
          forceAssign: analytics.force_assign,
        });
      }
    },
  })
);

const getNextRecord = fileToken => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_NEXT_RECORD',
      fail: 'FAILED_GET_NEXT_RECORD',
      end: 'FINISHED_GET_NEXT_RECORD',
    },
    method: 'GET',
    url: `/api/v1/receivers/${fileToken}/next_record`,
  })
);

const getReceivedByQuery = (query, signal, options = {}) => (
  crud({
    dispatch: inboxQueryDispatch({
      begin: 'BEGIN_GET_RECEIVED_RECORDS',
      fail: 'FAILED_GET_RECEIVED_RECORDS',
      end: 'FINISHED_GET_RECEIVED_RECORDS',
    }, options),
    method: 'GET',
    url: `/api/v1/receivers${query}`,
    query: options.silent ? undefined : query,
    signal,
  })
);

const getReferralsQuery = (query, signal, options = {}) => {
  let queryAttribute = query;
  if (queryAttribute.includes('?')) {
    queryAttribute = queryAttribute.replace('?', '');
  }

  return crud({
    dispatch: inboxQueryDispatch({
      begin: 'BEGIN_GET_RECEIVED_RECORDS',
      fail: 'FAILED_GET_RECEIVED_RECORDS',
      end: 'FINISHED_GET_RECEIVED_RECORDS',
    }, options),
    method: 'GET',
    url: `/api/v1/receivers?referrals=true&category=Referral&${queryAttribute}`,
    query: options.silent ? undefined : query,
    signal,
  });
};

const getReferralStatusCounts = (query = '', signal) => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_REFERRAL_STATUS_COUNTS',
      fail: 'FAILED_GET_REFERRAL_STATUS_COUNTS',
      end: 'FINISHED_GET_REFERRAL_STATUS_COUNTS',
    },
    method: 'GET',
    url: buildReferralStatusCountsUrl(query),
    signal,
  })
);

const getAssignedByQuery = (query, signal, options = {}) => (
  crud({
    dispatch: inboxQueryDispatch({
      begin: 'BEGIN_GET_ASSIGNED_RECORDS',
      fail: 'FAILED_GET_ASSIGNED_RECORDS',
      end: 'FINISHED_GET_ASSIGNED_RECORDS',
    }, options),
    method: 'GET',
    url: `/api/v1/assigned_documents${query}`,
    query: options.silent ? undefined : query,
    signal,
  })
);

const getAssignedRecord = assignedDocumentId => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_ASSIGNED_RECORD',
      failed: 'FAILED_GET_ASSIGNED_RECORD',
      end: 'FINISHED_GET_ASSIGNED_RECORD',
    },
    method: 'GET',
    url: `/api/v1/assigned_documents/${assignedDocumentId}`,
  })
);

const removeReceivedRecords = (tokens) => {
  return ({
    type: 'REMOVE_RECEIVED_RECORDS',
    payload: {
      tokens,
    },
  });
};

// After a successful (un)archive the affected rows no longer belong in the current
// view: clear the bulk selection (it may reference removed rows) and ask the inbox
// to refetch in the background so rows/counts reconcile without a page reload.
// No-op when the feature is off — the archive/unarchive UI then falls back to
// its legacy full page reload, which resets selection itself.
const archiveSettled = reason => (dispatch) => {
  if (!inboxBackgroundRefreshEnabled()) return;
  dispatch(rowSelected([]));
  dispatch(requestInboxTableRefresh(reason));
};

const archiveRecords = tokens => (
  crud({
    dispatch: {
      begin: 'BEGIN_ARCHIVE_RECORDS',
      fail: 'FAILED_ARCHIVE_RECORDS',
      end: 'FINISHED_ARCHIVE_RECORDS',
    },
    method: 'PATCH',
    url: '/api/v1/records',
    data: { tokens },
    callback: () => (dispatch) => {
      dispatch(removeReceivedRecords(tokens));
      dispatch(archiveSettled('archive'));
    },
  })
);

const unarchiveRecords = tokens => (
  crud({
    dispatch: {
      begin: 'BEGIN_UNARCHIVE_RECORD',
      fail: 'FAILED_UNARCHIVE_RECORD',
      end: 'FINISHED_UNARCHIVE_RECORD',
    },
    method: 'PATCH',
    url: '/api/v1/records/unarchive',
    data: { tokens },
    callback: () => archiveSettled('unarchive'),
  })
);

const archiveReceivedRecords = tokens => (
  crud({
    dispatch: {
      begin: 'BEGIN_ARCHIVE_RECORDS',
      fail: 'FAILED_ARCHIVE_RECORDS',
      end: 'FINISHED_ARCHIVE_RECORDS',
    },
    method: 'PATCH',
    url: '/api/v1/receivers',
    data: { tokens },
    callback: () => (dispatch) => {
      dispatch(removeReceivedRecords(tokens));
      dispatch(archiveSettled('archive'));
    },
  })
);

const unarchiveReceivedRecords = tokens => (
  crud({
    dispatch: {
      begin: 'BEGIN_UNARCHIVE_RECORD',
      fail: 'FAILED_UNARCHIVE_RECORD',
      end: 'FINISHED_UNARCHIVE_RECORD',
    },
    method: 'PATCH',
    url: '/api/v1/receivers/unarchive',
    data: { tokens },
    callback: () => archiveSettled('unarchive'),
  })
);

const getStatusOfSentRecordById = (recordId, sendToken) => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_SINGLE_RECORD_STATUS',
      fail: 'FAILED_GET_SINGLE_RECORD_STATUS',
      end: 'FINISHED_GET_SINGLE_RECORD_STATUS',
    },
    method: 'GET',
    url: `/api/v1/records/${recordId}/send/${sendToken}`,
  })
);

const getStatusOfSentPatientRecordById = (recordId, sendToken) => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_SINGLE_RECORD_STATUS',
      fail: 'FAILED_GET_SINGLE_RECORD_STATUS',
      end: 'FINISHED_GET_SINGLE_RECORD_STATUS',
    },
    method: 'GET',
    url: `/api/v1/records/${recordId}/send/${sendToken}?patient=true`,
  })
);

/** Marks sent data as revoked in the store after backend call
 * @param {String} recordId the id of the record to revoke
 * @param {String} sendToken identifies the sent transmission to revoke
 */
const removeSentData = (recordId, sendToken) => {
  return ({
    type: 'REMOVE_SENT_RECORD',
    payload: {
      recordId,
      sendToken,
    },
  });
};

/** Updates the store's uploaded data after request
 * @param {String} recordId - the id of the record to update
 * @param {String} patientDOB - the  updated DOB
 * @param {String} patientName - the updated patient's name
 * @param {String} patientFirstName - the updated patient's first name
 * @param {String} patientMiddleName - the updated patient's middle name
 * @param {String} patientLastName - the updated patient's last name
 * @param {String} patientPhoneNumber - the updated patient's phone number
 */
const updateUploadedData = (
  recordId,
  patientDOB,
  patientName,
  patientFirstName,
  patientMiddleName,
  patientLastName,
  patientPhoneNumber,
  mode,
  privacyLevel,
  incorrectPatientInfo,
  documentTitle,
  documentCategory,
  referralNote,
  referringProvider,
  referringProviderOffice,
) => {
  toastr.success('You have successfully updated the record');
  if (documentCategory === 'None') {
    // eslint-disable-next-line no-param-reassign
    documentCategory = null;
  }
  return ({
    type: 'UPDATE_UPLOADED_RECORDS',
    payload: {
      recordId,
      patientDOB,
      patientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      patientPhoneNumber,
      mode,
      privacyLevel,
      incorrectPatientInfo,
      documentTitle,
      documentCategory,
      referralNote,
      referringProvider,
      referringProviderOffice,
    },
  });
};

const updateUploadedRecordPrivacy = (recordId, mode, privacyLevel) => ({
  type: 'UPDATE_UPLOADED_RECORD_PRIVACY',
  payload: {
    recordId,
    mode,
    privacyLevel,
  },
});

/** Call action to update the record info
 * @param {String} recordId - the id of the record to update
 * @param {String} patientDOB - the  updated DOB
 * @param {String} patientName - the updated patient's name
 */
const updatePatientInfo = (
  recordId,
  patientDOB,
  patientFirstName,
  patientMiddleName,
  patientLastName,
  patientPhoneNumber,
  mode,
  privacyLevel,
  fromPage,
  isDOBIncorrect,
  isNameIncorrect,
  documentTitle,
  documentCategory,
  referralNote,
  referringProvider,
  referringProviderOffice,
  privacyOnly = false,
) => (
  crud({
    dispatch: {
      begin: 'BEGIN_UPDATE_RECORD',
      fail: 'FAILED_UPDATE_RECORD',
      end: 'FINISHED_UPDATE_RECORD',
    },
    method: 'PUT',
    data: {
      record: privacyOnly ? {
        privacy_level: privacyLevel,
      } : {
        patient_dob: patientDOB,
        patient_first_name: patientFirstName?.trim(),
        patient_middle_name: patientMiddleName?.trim(),
        patient_last_name: patientLastName?.trim(),
        patient_phone_number: patientPhoneNumber?.trim(),
        privacy_level: privacyLevel,
        is_patient_dob_incorrect: isDOBIncorrect,
        is_patient_name_incorrect: isNameIncorrect,
        document_title: documentTitle,
        document_category_id: documentCategory,
        referral_note: referralNote,
        referring_provider: referringProvider?.trim(),
        provider_office: referringProviderOffice?.trim(),
      },
    },
    url: `/api/v1/records/${recordId}?from=${fromPage}`,
    callback: () => {
      if (privacyOnly) {
        toastr.success('You have successfully updated the record');
        return updateUploadedRecordPrivacy(recordId, mode, privacyLevel);
      }

      const patientName = [patientFirstName, patientMiddleName, patientLastName]
        .filter(name => name && name.trim())
        .join(' ').trim();
      return updateUploadedData(
        recordId,
        patientDOB,
        patientName,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        patientPhoneNumber,
        mode,
        privacyLevel,
        isDOBIncorrect || isNameIncorrect,
        documentTitle,
        documentCategory,
        referralNote,
        referringProvider,
        referringProviderOffice,
      );
    },
  })
);

let nextEmailAttachmentPatientRequestId = 0;

const allocateEmailAttachmentPatientRequestId = () => {
  nextEmailAttachmentPatientRequestId += 1;
  return nextEmailAttachmentPatientRequestId;
};

const updateEmailAttachmentPatientInfo = ({
  fileToken,
  changes,
  fromPage,
}) => {
  const requestId = allocateEmailAttachmentPatientRequestId();

  return crud({
    dispatch: {
      begin: 'BEGIN_UPDATE_EMAIL_ATTACHMENT_PATIENT',
      fail: 'FAILED_UPDATE_EMAIL_ATTACHMENT_PATIENT',
      end: 'FINISHED_UPDATE_EMAIL_ATTACHMENT_PATIENT',
      meta: {
        fileToken,
        requestId,
      },
    },
    method: 'PUT',
    data: {
      record: changes,
      return_patient_metadata: true,
    },
    url: `/api/v1/records/${fileToken}?from=${fromPage}`,
  });
};

const reportEmailAttachmentPatientUpdateError = (fileToken, error) => {
  const meta = {
    fileToken,
    requestId: allocateEmailAttachmentPatientRequestId(),
  };

  return (dispatch) => {
    dispatch({
      type: 'BEGIN_UPDATE_EMAIL_ATTACHMENT_PATIENT',
      meta,
    });
    dispatch({
      type: 'FAILED_UPDATE_EMAIL_ATTACHMENT_PATIENT',
      payload: error,
      meta,
    });
    return error;
  };
};

const updateSelectedRecordLabels = (fileToken, labels) => ({
  type: 'UPDATE_SELECTED_RECORD_LABELS',
  payload: { fileToken, labels },
});

const updateReceivedData = (sendToken, tag, response) => {
  if (response.status !== 204) {
    return ({
      type: 'UPDATE_RECEIVED_RECORDS',
      payload: {
        sendToken,
        tag,
      },
    });
  }
  return ({
    type: 'IGNORE_RECEIVER_UPDATE',
  });
};

const updateReceivedLabels = (sendToken, labels, response, isLabelIncorrect = false) => {
  if (response.status !== 500) {
    return ({
      type: 'UPDATE_RECEIVED_RECORDS',
      payload: {
        sendToken,
        labels,
        isLabelIncorrect,
      },
    });
  }
  return ({
    type: 'IGNORE_RECEIVER_UPDATE',
  });
};

const markRecordAsViewed = (sendToken) => {
  return ({
    type: 'MARK_RECORD_AS_VIEWED',
    payload: {
      sendToken,
    },
  });
};

/** Adds/updates the tag for a received record.
 * @param {String} recordId The file token of the record to update
 * @param {String} tag The tag to assign to the received record
 */
const editReceivedRecordTag = (sendToken, tag) => (
  crud({
    dispatch: {
      begin: 'BEGIN_EDIT_NOTIFY',
      fail: 'FAILED_EDIT_NOTIFY',
      end: 'FINISHED_EDIT_NOTIFY',
    },
    method: 'PUT',
    data: {
      receiver: {
        tag,
        send_token: sendToken,
      },
    },
    url: '/api/v1/receivers/update_tags',
    callback: response => (dispatch) => {
      // Patch the visible row immediately, then refetch in the background: a
      // notify can reassign the record to another department, so the row may no
      // longer belong in the current view (and filtered lists/counts go stale
      // without a refetch).
      dispatch(updateReceivedData(sendToken, tag, response));
      dispatch(requestInboxTableRefresh('notify'));
    },
    errorHandler: error => ({ response: error.response }),
    onSuccess: () => {
      if (process.env.NODE_ENV !== 'development' && posthog) {
        posthog.capture('edit_received_record_tag', {
          sendToken,
          tag,
        });
      }
    },
  })
);

const editReceivedRecordLabels = (sendToken, labelIds, labels, receiverType, isLabelIncorrect = false, correctLabel = null, multipleReceiverUpdate = false) => (
  crud({
    dispatch: {
      begin: 'BEGIN_EDIT_RECEIVED_RECORD_LABELS',
      fail: 'FAILED_EDIT_RECEIVED_RECORD_LABELS',
      end: 'FINISHED_EDIT_RECEIVED_RECORD_LABELS',
    },
    method: 'POST',
    data: {
      ids: labelIds,
      is_label_incorrect: isLabelIncorrect,
      correct_label: correctLabel,
      multiple_receiver_update: multipleReceiverUpdate,
    },
    url: `/api/v1/receivers/${sendToken}/labels?receiver_type=${receiverType}`,
    callback: response => updateReceivedLabels(sendToken, labels, response, isLabelIncorrect),
  })
);

// Teams (v3): set, change, or clear the single team tag on a record (D1/D7).
// `teamSlug` is a team SLUG, or null/'' to clear. Mirrors editReceivedRecordLabels'
// receiver-scoped POST; the caller refetches the current page query on success so
// the row + any active team-view filter reflect the saved state.
const setRecordTeam = (sendToken, teamSlug, receiverType) => (
  crud({
    dispatch: {
      begin: 'BEGIN_SET_RECORD_TEAM',
      fail: 'FAILED_SET_RECORD_TEAM',
      end: 'FINISHED_SET_RECORD_TEAM',
    },
    method: 'POST',
    data: {
      team_id: teamSlug || '',
    },
    url: `/api/v1/receivers/${sendToken}/team?receiver_type=${receiverType}`,
  })
);

// Teams (v3) bulk: set, change, or clear the team tag on many records at once (D4).
// `teamSlug` is a team SLUG, or null/'' to clear; `records` is [{ id: send_token,
// receiver_type }]. Mirrors setRecordTeam's crud() style; the caller refetches the
// current page query and clears the selection on success. Returns { updated, skipped }.
const setRecordTeamBulk = (records, teamSlug) => (
  crud({
    dispatch: {
      begin: 'BEGIN_SET_RECORD_TEAM_BULK',
      fail: 'FAILED_SET_RECORD_TEAM_BULK',
      end: 'FINISHED_SET_RECORD_TEAM_BULK',
    },
    method: 'POST',
    data: {
      team_id: teamSlug || '',
      records,
    },
    url: '/api/v1/receivers/team_bulk',
  })
);

const getRecordLink = recordId => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_RECORD_LINK',
      fail: 'FAILED_GET_RECORD_LINK',
      end: 'FINISHED_GET_RECORD_LINK',
    },
    method: 'POST',
    url: `/api/v1/records/${recordId}/link`,
  })
);

const getSecureSignedURL = (recordId, token, source) => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_SECURE_URL',
      fail: 'FAILED_GET_SECURE_URL',
      end: 'FINISHED_GET_SECURE_URL',
    },
    method: 'GET',
    url: `/api/v1/records/${recordId}/link/${token}?source=${source}`,
  })
);

const submitForm = (recordId, token, data) => (
  crud({
    dispatch: {
      begin: 'BEGIN_SUBMIT_FORM',
      fail: 'FAILED_SUBMIT_FORM',
      end: 'FINISHED_SUBMIT_FORM',
    },
    method: 'POST',
    data,
    url: `/api/v1/records/${recordId}/submit_form`,
    callback: () => getSecureSignedURL(recordId, token, 'web'),
  })
);

const printOrDownloadSecureLink = (recordId, token) => (
  crud({
    dispatch: {
      begin: 'BEGIN_PRINT_SECURE_URL',
      fail: 'FAILED_PRINT_SECURE_URL',
      end: 'FINISHED_PRINT_SECURE_URL',
    },
    method: 'GET',
    url: `/api/v1/records/${recordId}/link/${token}?print=1`,
  })
);

const clearDownloadUrl = () => {
  return {
    type: 'CLEAR_DOWNLOAD_URL',
  };
};

/** Sends call to backend to resend the record to receiver.
 * @param {Object} recipient { file_token, send_token }
 */
const resendRecord = recipient => (
  crud({
    dispatch: {
      begin: 'BEGIN_RESEND_RECORD',
      fail: 'FAILED_RESEND_RECORD',
      end: 'FINISHED_RESEND_ONE_RECORD',
    },
    method: 'PUT',
    data: {
      recipient: {
        send_token: recipient.send_token,
      },
    },
    url: `/api/v1/records/${recipient.file_token}/resend/`,
  })
);

/** Sends call to backend to batch resend records.
 * @param {String} days -The no. of days the records have not been viewed.
 */
const bulkResend = days => (
  crud({
    dispatch: {
      begin: 'BEGIN_RESEND_RECORD',
      fail: 'FAILED_RESEND_RECORD',
      end: 'FINISHED_RESEND_RECORD',
    },
    method: 'PUT',
    url: `/api/v1/records/resend/${days}`,
  })
);

/** Sends call to the backend to delete receiver
 * @param {String} recordId the id of the record to revoke
 * @param {String} sendToken identifies the receiver
 */
const revokeRecordAcessById = (recordId, sendToken) => (
  crud({
    dispatch: {
      begin: 'BEGIN_REVOKE_RECORD',
      fail: 'FAILED_REVOKE_RECORD',
      end: 'FINISHED_REVOKE_RECORD',
    },
    method: 'PUT',
    data: {
      receiver: {
        send_token: sendToken,
      },
    },
    url: `/api/v1/records/${recordId}/send/`,
    callback: () => removeSentData(recordId, sendToken),
  })
);

/** Uploads record through endpoint in rails.
 * @param {FormData} body
 */
const uploadRecord = body => (
  crud({
    dispatch: {
      begin: 'BEGIN_UPLOAD_RECORD',
      fail: 'FAILED_UPLOAD_RECORD',
      end: 'FINISHED_UPLOAD_RECORD',
    },
    method: 'POST',
    data: body,
    url: '/api/v1/records/upload',
  })
);

/**
 * Fetch the organization fax lines a user may target when ingesting an upload
 * into Referral Management. These are surfaced to the user as departments/queues
 * (labeled by owner), never as raw "fax lines".
 * @return {Promise} resolves to the axios response ({ data: { fax_numbers: [{ id, number, owner_type, owner_name }] } })
 *                   or an error object ({ error: true, response }) from crud.
 */
const getReferralIngestionTargets = () => (
  crud({
    dispatch: {
      // No reducers listen for these — the caller consumes the returned promise
      // directly (mirrors mergeRecords/duplicateRecord usage).
      begin: 'BEGIN_GET_REFERRAL_INGESTION_TARGETS',
      fail: 'FAILED_GET_REFERRAL_INGESTION_TARGETS',
      end: 'FINISHED_GET_REFERRAL_INGESTION_TARGETS',
    },
    method: 'GET',
    url: '/api/v1/fax_numbers/referral_ingestion_targets',
  })
);

/**
 * Ingest an uploaded record into Referral Management server-side.
 * @param {String} recordId - the record's file_token (matches records member routes)
 * @param {Object} body - { fax_number_id, referring_provider?, referring_provider_office?, referring_provider_fax?, note? }
 * @return {Promise} resolves to the axios response ({ data: { record, referral_entity_slug, send_token } })
 *                   or an error object ({ error: true, response }) from crud.
 */
const moveRecordToReferralManagement = (recordId, body) => (
  crud({
    dispatch: {
      begin: 'BEGIN_MOVE_TO_REFERRAL_MANAGEMENT',
      fail: 'FAILED_MOVE_TO_REFERRAL_MANAGEMENT',
      end: 'FINISHED_MOVE_TO_REFERRAL_MANAGEMENT',
    },
    method: 'POST',
    url: `/api/v1/records/${recordId}/move_to_referral_management`,
    data: body,
  })
);

/**
 * Trigger a manual AI run on an uploaded record.
 * @param {String} recordId - the record's file_token
 * @return {Promise} resolves to the axios response or an error object from crud.
 */
const runAiOnUploadedRecord = recordId => (
  crud({
    dispatch: {
      begin: 'BEGIN_RUN_AI_ON_UPLOAD',
      fail: 'FAILED_RUN_AI_ON_UPLOAD',
      end: 'FINISHED_RUN_AI_ON_UPLOAD',
    },
    method: 'POST',
    url: `/api/v1/records/${recordId}/run_ai`,
  })
);

/**
 * Let backend know that user printed record
 * @param  {Object} data { access_code, fax_code }
 */
const printGuestRecord = data => (
  crud({
    dispatch: {
      // None of these reducers exist because no state on the frontend
      // needs to be changed
      //
      // The function simply sends data which goes into the Pub/Sub pipeline
      begin: 'BEGIN_PRINT_GUEST_RECORD',
      fail: 'FAILED_PRINT_GUEST_RECORD',
      end: 'FINISHED_PRINT_GUEST_RECORD',
    },
    method: 'POST',
    url: '/api/v1/print',
    data,
  })
);

const printPatientRecord = data => (
  crud({
    dispatch: {
      begin: 'BEGIN_PRINT_GUEST_RECORD',
      fail: 'FAILED_PRINT_GUEST_RECORD',
      end: 'FINISHED_PRINT_GUEST_RECORD',
    },
    method: 'POST',
    url: '/api/v1/patients/print',
    data,
  })
);

/**
 * Concatenate multiple records together into a new record.
 * The original records aren't affected.
 * @param  {string[]} tokens[] - The file tokens to be merged.
 */
const mergeRecords = tokens => (
  crud({
    dispatch: {
      begin: 'BEGIN_MERGE_RECORDS',
      fail: 'FAILED_MERGE_RECORDS',
      end: 'FINISHED_MERGE_RECORDS',
    },
    method: 'POST',
    url: '/api/v1/records/merge',
    data: { tokens },
  })
);

/**
 * Concatenate multiple referrals together into a new record.
 * The original records aren't affected.
 * @param  {string[]} tokens[] - The send tokens to be merged.
 * @param  {Object} [mergeOverrides] - Optional retained-value overrides chosen
 *   in the merge confirmation modal. Shape:
 *   { document_category_id, field_sources: { document_date, fax_number,
 *   patient, document_title } }. When omitted, the backend defaults every
 *   retained field to the oldest selected document (legacy behavior).
 */
const mergeReferrals = (tokens, mergeOverrides = null) => (
  crud({
    dispatch: {
      begin: 'BEGIN_MERGE_REFERRALS',
      fail: 'FAILED_MERGE_REFERRALS',
      end: 'FINISHED_MERGE_REFERRALS',
    },
    method: 'POST',
    url: '/api/v1/receivers/merge',
    data: mergeOverrides ? { tokens, merge_overrides: mergeOverrides } : { tokens },
    onSuccess: () => {
      if (process.env.NODE_ENV !== 'development' && posthog) {
        posthog.capture('merge_referrals', {
          tokens,
        });
      }
    },
  })
);

/**
 * Fetches the per-document field values, computed default category, and the
 * org's selectable categories used to populate the merge confirmation modal.
 * @param  {string[]} tokens[] - The send tokens to be merged.
 */
const getMergePreview = tokens => (
  crud({
    dispatch: {
      begin: 'BEGIN_MERGE_PREVIEW',
      fail: 'FAILED_MERGE_PREVIEW',
      end: 'FINISHED_MERGE_PREVIEW',
    },
    method: 'POST',
    url: '/api/v1/receivers/merge_preview',
    data: { tokens },
  })
);

const resendAllFailedFaxes = () => (
  crud({
    dispatch: {
      begin: 'BEGIN_RESEND_ALL_FAILED_FAXES',
      fail: 'FAILED_RESEND_ALL_FAILED_FAXES',
      end: 'FINISHED_RESEND_ALL_FAILED_FAXES',
    },
    method: 'POST',
    url: '/api/v1/records/sent/resend_all_failed',
  })
);

const printUserRecord = record => async (dispatch) => {
  try {
    const response = await axios({
      method: 'GET',
      url: `/api/v1/print/${record}`,
      headers: {
        'X-CSRF-TOKEN': document.getElementsByName('csrf-token')[0].content,
      },
    });
    dispatch({
      type: 'FINISHED_PRINT_USER_RECORD',
      payload: response,
    });
    if (response.status === 201) {
      if (process.env.NODE_ENV !== 'development' && posthog) {
        posthog.capture('print_user_record', {
          recordId: record,
        });
      }
      if (response.data?.signed_url) {
        return {
          signedUrl: response.data.signed_url,
          downloadFilename: response.data.download_filename || 'file',
        };
      }
      return null;
    }
    return null;
  } catch (error) {
    dispatch({
      type: 'FAILED_PRINT_USER_RECORD',
    });
    return null;
  }
};

const printSentRecord = (sendToken) => async (dispatch) => {
  try {
    const response = await axios({
      method: 'GET',
      url: `/api/v1/records/sent/${sendToken}?print=true`,
      headers: {
        'X-CSRF-TOKEN': document.getElementsByName('csrf-token')[0].content,
      },
    });
    dispatch({
      type: 'FINISHED_PRINT_USER_RECORD',
      payload: response,
    });
    if (response.status === 201) {
      if (response.data?.signed_url) {
        return {
          signedUrl: response.data.signed_url,
          downloadFilename: response.data.download_filename || 'file',
        };
      }
      return null;
    }
    return null;
  } catch (error) {
    dispatch({
      type: 'FAILED_PRINT_USER_RECORD',
    });
    return null;
  }
};

const downloadZip = tokens => async (dispatch) => {
  try {
    const response = await axios({
      method: 'POST',
      url: '/api/v1/records/download',
      responseType: 'arraybuffer',
      data: { tokens },
      headers: {
        'X-CSRF-TOKEN': document.getElementsByName('csrf-token')[0].content,
        'Content-Type': 'application/json',
        Accept: 'application/zip',
      },
      onDownloadProgress: (progress) => {
        dispatch({
          type: 'PROGRESS_DOWNLOAD_RECORDS_ZIP',
          payload: progress,
        });
      },
    });
    const currentDate = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    downloadToBrowser(response, `medsender_records_download_${currentDate}`, 'zip');
  } catch (error) {
    dispatch({
      type: 'FAILED_DOWNLOAD_RECORDS_ZIP',
      payload: {
        error:
          `We\'re sorry, but something went wrong and the download failed. Please try again
            or contact support if the issue persists.
          `,
      },
    });
  }
};

const saveAnnotationsOnRecord = (record, id) => (
  crud({
    dispatch: {
      begin: 'BEGIN_SAVE_ANNOTATIONS_ON_RECORD',
      fail: 'FAILED_SAVE_ANNOTATIONS_ON_RECORD',
      end: 'FINISHED_SAVE_ANNOTATIONS_ON_RECORD',
    },
    method: 'POST',
    url: `/api/v1/records/${id}/create_edited_record`,
    data: record,
  })
);

const setUnsavedAnnotationsOnRecord = () => (dispatch) => {
  dispatch({
    type: 'SET_UNSAVED_EDITS_ON_RECORD',
  });
};

const updateSignatures = data => (
  crud({
    dispatch: {
      begin: 'BEGIN_SAVE_SIGNATURE',
      fail: 'FAILED_SAVE_SIGNATURE',
      end: 'FINISHED_SAVE_SIGNATURE',
    },
    method: 'PATCH',
    url: '/api/v1/signatures/',
    data,
  })
);

const getSignature = () => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_SIGNATURE',
      fail: 'FAILED_GET_SIGNATURE',
      end: 'FINISHED_GET_SIGNATURE',
    },
    method: 'GET',
    url: '/api/v1/signatures/',
  })
);

const getAttachments = () => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_ATTACHMENTS',
      fail: 'FAILED_GET_ATTACHMENTS',
      end: 'FINISHED_GET_ATTACHMENTS',
    },
    method: 'GET',
    url: '/api/v1/signature_attachments/',
  })
);

const getReferralReplyUrl = (url) => {
  return crud({
    dispatch: {
      begin: 'BEGIN_GET_REFERRAL_REPLY_FAX_URL',
      fail: 'FAILED_GET_REFERRAL_REPLY_FAX_URL',
      end: 'FINISHED_GET_REFERRAL_REPLY_FAX_URL',
    },
    method: 'GET',
    url,
  });
};

const getReferralReplyPreviewUrl = (sendToken, patientName, patientDOB, referringProvider, referralTemplate, appointmentDate, resentFileName) => {
  if (!sendToken) {
    return {
      type: 'FAILED_GET_REFERRAL_REPLY_PREVIEW_URL',
      payload: {
        isLoading: false,
        error: true,
      },
    };
  }

  const params = new URLSearchParams({
    patient_name: patientName,
    patient_dob: patientDOB,
    referring_provider: referringProvider,
    referral_template: referralTemplate,
  });
  if (resentFileName) params.append('resent_file_name', resentFileName);
  if (appointmentDate) params.append('appointment_date_local', appointmentDate);

  const url = `/api/v1/receivers/${sendToken}/referral_replies/preview?${params.toString()}`;

  return crud({
    dispatch: {
      begin: 'BEGIN_GET_REFERRAL_REPLY_PREVIEW_URL',
      fail: 'FAILED_GET_REFERRAL_REPLY_PREVIEW_URL',
      end: 'FINISHED_GET_REFERRAL_REPLY_PREVIEW_URL',
    },
    method: 'GET',
    url,
  });
};

const getReferralReplies = sendToken => (
  crud({
    dispatch: {
      begin: 'BEGIN_GET_REFERRAL_REPLIES',
      fail: 'FAILED_GET_REFERRAL_REPLIES',
      end: 'FINISHED_GET_REFERRAL_REPLIES',
    },
    method: 'GET',
    url: `/api/v1/receivers/${sendToken}/referral_replies`,
  })
);

const sendReferralReply = ({
  sendToken,
  selectedTemplate,
  callerName,
  patientDOB,
  patientName,
  patientFirstName,
  patientMiddleName,
  patientLastName,
  referringProvider,
  resentReplySlug,
  resendDetailsChanged,
  appointmentDate,
  localAppointmentDate,
}) => (
  crud({
    dispatch: {
      begin: 'BEGIN_SEND_REFERRAL_REPLY',
      fail: 'FAILED_SEND_REFERRAL_REPLY',
      end: 'FINISH_SEND_REFERRAL_REPLY',
    },
    method: 'POST',
    url: `/api/v1/receivers/${sendToken}/referral_replies`,
    data: {
      referral_template: selectedTemplate,
      fax_number: callerName,
      patient_dob: patientDOB,
      patient_name: patientName,
      patient_first_name: patientFirstName,
      patient_middle_name: patientMiddleName,
      patient_last_name: patientLastName,
      referring_provider: referringProvider,
      resent_reply_slug: resentReplySlug,
      resend_details_changed: resendDetailsChanged,
      appointment_date_utc: appointmentDate,
      appointment_date_local: localAppointmentDate,
    },
    onSuccess: () => {
      if (process.env.NODE_ENV !== 'development' && posthog) {
        posthog.capture('referral_reply_sent', {
          sendToken,
          selectedTemplate,
          callerName,
          patientDOB,
          patientName,
          patientFirstName,
          patientMiddleName,
          patientLastName,
          referringProvider,
          resentReplySlug,
          resendDetailsChanged,
          appointmentDate,
          localAppointmentDate,
        });
      }
    },
  })
);

const saveAttachment = (blob, attachmentId) => (
  crud({
    dispatch: {
      begin: 'BEGIN_CREATE_ATTACHMENTS',
      fail: 'FAILED_CREATE_ATTACHMENTS',
      end: 'FINISHED_CREATE_ATTACHMENTS',
    },
    method: 'POST',
    url: '/api/v1/signature_attachments',
    data: {
      blob,
      attachment_id: attachmentId,
    },
  })
);

const deleteAttachment = attachmentId => (
  crud({
    dispatch: {
      begin: 'BEGIN_CREATE_ATTACHMENTS',
      fail: 'FAILED_CREATE_ATTACHMENTS',
      end: 'FINISHED_CREATE_ATTACHMENTS',
    },
    method: 'DELETE',
    url: `/api/v1/signature_attachments/${attachmentId}`,
  })
);

// Helper to poll split status until completion or failure
const pollSplitStatus = async (recordId, jobToken, dispatch, maxAttempts = 60, intervalMs = 2000) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await axios({
        method: 'GET',
        url: `/api/v1/records/${recordId}/split_status?job_token=${jobToken}`,
        headers: {
          'X-CSRF-TOKEN': document.getElementsByName('csrf-token')[0].content,
        },
      });

      const { status } = response.data;

      if (status === 'completed') {
        dispatch({
          type: 'FINISH_SPLIT_RECORD',
          payload: response,
        });
        return response;
      }

      if (status === 'failed') {
        const errorMessage = response.data.error_message || 'Split operation failed';
        dispatch({
          type: 'FAILED_SPLIT_RECORD',
          payload: {
            response: {
              status: 500,
              data: { error: errorMessage },
            },
          },
        });
        return { error: true, message: errorMessage };
      }

      // Still processing, wait and try again
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    } catch (error) {
      // On network error, continue polling unless max attempts reached
      if (attempt === maxAttempts - 1) {
        dispatch({
          type: 'FAILED_SPLIT_RECORD',
          payload: {
            response: {
              status: 500,
              data: { error: 'Failed to check split status' },
            },
          },
        });
        return { error: true, message: 'Failed to check split status' };
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }

  // Timeout
  dispatch({
    type: 'FAILED_SPLIT_RECORD',
    payload: {
      response: {
        status: 500,
        data: { error: 'Split operation timed out' },
      },
    },
  });
  return { error: true, message: 'Split operation timed out' };
};

const splitUploadedRecord = (fileToken, pageToSplit, patientFirstName, patientLastName, patientMiddleName, dob) => (
  async (dispatch) => {
    dispatch({ type: 'BEGIN_SPLIT_RECORD' });

    try {
      // Start the async split job
      const response = await axios({
        method: 'POST',
        url: `/api/v1/records/${fileToken}/split`,
        data: {
          num_pages: pageToSplit,
          patient_first_name: patientFirstName,
          patient_last_name: patientLastName,
          patient_middle_name: patientMiddleName,
          patient_dob: dob,
        },
        headers: {
          'X-CSRF-TOKEN': document.getElementsByName('csrf-token')[0].content,
        },
      });

      const { job_token: jobToken } = response.data;

      // Poll for completion
      return pollSplitStatus(fileToken, jobToken, dispatch);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to start split operation';
      dispatch({
        type: 'FAILED_SPLIT_RECORD',
        payload: {
          response: {
            status: error.response?.status || 500,
            data: { error: errorMessage },
          },
        },
      });
      return { error: true, message: errorMessage };
    }
  }
);

// Received records still use synchronous split (receivers controller not yet updated)
const splitReceivedRecord = (token, pageToSplit, patientFirstName, patientLastName, patientMiddleName, dob) => (
  crud({
    dispatch: {
      begin: 'BEGIN_SPLIT_RECORD',
      fail: 'FAILED_SPLIT_RECORD',
      end: 'FINISH_SPLIT_RECORD',
    },
    method: 'POST',
    url: `/api/v1/receivers/${token}/split`,
    data: {
      num_pages: pageToSplit,
      patient_first_name: patientFirstName,
      patient_last_name: patientLastName,
      patient_middle_name: patientMiddleName,
      patient_dob: dob,
    },
  })
);

const triggerSFTPUpload = sendToken => (
  crud({
    dispatch: {
      begin: 'BEGIN_UPLOAD_TO_SFTP_FOLDER',
      fail: 'FAILED_UPLOAD_TO_SFTP_FOLDER',
      end: 'FINISH_UPLOAD_TO_SFTP_FOLDER',
    },
    method: 'POST',
    url: `/api/v1/receivers/${sendToken}/sftp_uploads`,
    callback: () => chartUploadBackgroundRefresh(),
  })
);

const triggerAutoUpload = ({
  sendToken,
  receiverType,
  documentCategory = '',
  fileName = '',
  patientName = '',
  patientFirstName = '',
  patientLastName = '',
  patientMiddleName = '',
  secondaryPhone = '',
  patientDOB = '',
  patientMrn = '',
  patientPhoneNumber = '',
  patientGender = '',
  patientAddress = '',
  patientZipCode = '',
  patientCity = '',
  practitionerName = '',
  chartCreationEnabled = false,
  patientEmail = '',
  patientState = '',
  referringProvider = '',
  providerOffice = '',
  primaryMemberId = '',
  primaryPlan = '',
  primaryPlanName = '',
  practiceName = '',
  primaryPolicyType = '',
  primaryGroupNumber = '',
  assignTo = '',
  createTaskAfterUpload = false,
  taskCreationMode = null,
  task = '',
  taskDetails = '',
  priority = '',
  dueDate = '',
  notes = '',
  secondaryPlan = '',
  secondaryPlanName = '',
  secondaryMemberId = '',
  secondaryPolicyType = '',
  secondaryGroupNumber = '',
  insuranceEffectiveDate = '',
  documentType = '',
  dateOfService = '',
  referringProviderId = '',
  facilityName = '',
  marketingRep = '',
  icd10Codes = '',
  primaryPayerAddress = '',
  primaryPayerState = '',
  secondaryPayerAddress = '',
  secondaryPayerState = '',
  orderingProvider = '',
  fromReferralManagement = false,
  caseName = '',
  emrPatientId = null,
  authorizationType = '',
  authorizationNumber = '',
  authorizationDescription = '',
  authorizationActiveStatus = false,
  authorizationUnlimitedVisits = false,
  authorizationNumberOfVisits = '',
  authorizationFromDate = '',
  authorizationToDate = '',
  authorizationNote = '',
  authorizationReferringProvider = '',
}) => (
  crud({
    dispatch: {
      begin: 'BEGIN_AUTOUPLOAD',
      fail: 'FAILED_AUTOUPLOAD',
      end: 'FINISH_AUTOUPLOAD',
    },
    method: 'POST',
    url: `/api/v1/receivers/${sendToken}/emr_uploads`,
    data: {
      receiver_type: receiverType,
      document_category: documentCategory,
      file_name: fileName,
      patient_name: patientName,
      patient_first_name: patientFirstName,
      patient_middle_name: patientMiddleName,
      patient_last_name: patientLastName,
      secondary_phone: secondaryPhone,
      patient_dob: patientDOB,
      patient_mrn: patientMrn,
      patient_gender: patientGender,
      patient_phone_number: patientPhoneNumber,
      patient_address: patientAddress,
      patient_zip_code: patientZipCode,
      patient_city: patientCity,
      practitioner_name: (chartCreationEnabled && assignTo) ? assignTo : practitionerName,
      create_chart_enabled: chartCreationEnabled,
      patient_email: patientEmail,
      patient_state: patientState,
      referring_provider: referringProvider,
      provider_office: providerOffice,
      primary_member_id: primaryMemberId,
      primary_plan: primaryPlan,
      primary_plan_name: primaryPlanName,
      practice_name: practiceName,
      policy_type: primaryPolicyType,
      group_number: primaryGroupNumber,
      assign_to: (createTaskAfterUpload || taskCreationMode === 'custom') ? assignTo : '',
      task_creation_mode: taskCreationMode,
      task,
      task_details: taskDetails,
      due_date: dueDate,
      priority,
      notes,
      secondary_plan: secondaryPlan,
      secondary_plan_name: secondaryPlanName,
      secondary_member_id: secondaryMemberId,
      secondary_policy_type: secondaryPolicyType,
      secondary_group_number: secondaryGroupNumber,
      insurance_effective_date: insuranceEffectiveDate,
      document_type: documentType,
      date_of_service: dateOfService,
      referring_provider_id: referringProviderId,
      facility_name: facilityName,
      marketing_rep: marketingRep,
      icd_codes: icd10Codes,
      primary_insurance_address: primaryPayerAddress,
      primary_insurance_state: primaryPayerState,
      secondary_insurance_address: secondaryPayerAddress,
      secondary_insurance_state: secondaryPayerState,
      ordering_provider: orderingProvider,
      from_referral_management: fromReferralManagement,
      case_name: caseName,
      emr_patient_id: emrPatientId,
      authorization_type: authorizationType,
      authorization_number: authorizationNumber,
      authorization_description: authorizationDescription,
      authorization_active_status: authorizationActiveStatus,
      authorization_unlimited_visits: authorizationUnlimitedVisits,
      authorization_number_of_visits: authorizationNumberOfVisits,
      authorization_from_date: authorizationFromDate,
      authorization_to_date: authorizationToDate,
      authorization_note: authorizationNote,
      authorization_referring_provider: authorizationReferringProvider,
    },
    onSuccess: () => {
      if (process.env.NODE_ENV !== 'development' && posthog) {
        posthog.capture('trigger_auto_upload', {
          receiverType,
          documentCategory,
          fileName,
          patientName,
          patientFirstName,
          patientMiddleName,
          patientLastName,
          secondaryPhone,
          patientDOB,
          patientMrn,
          patientGender,
          patientPhoneNumber,
          patientAddress,
          patientZipCode,
          patientCity,
          practitionerName: (chartCreationEnabled && assignTo) ? assignTo : practitionerName,
          chartCreationEnabled,
          patientEmail,
          patientState,
          referringProvider,
          providerOffice,
          primaryMemberId,
          primaryPlan,
          primaryPlanName,
          practiceName,
          primaryPolicyType,
          primaryGroupNumber,
          task,
          taskDetails,
          dueDate,
          priority,
          notes,
          secondaryPlan,
          secondaryPlanName,
          secondaryMemberId,
          secondaryPolicyType,
          secondaryGroupNumber,
          insuranceEffectiveDate,
          documentType,
          dateOfService,
          referringProviderId,
          facilityName,
          marketingRep,
          icd10Codes,
          primaryPayerAddress,
          primaryPayerState,
          secondaryPayerAddress,
          secondaryPayerState,
          orderingProvider,
          authorizationType,
          authorizationNumber,
          authorizationDescription,
          authorizationActiveStatus,
          authorizationUnlimitedVisits,
          authorizationNumberOfVisits,
          authorizationFromDate,
          authorizationToDate,
          authorizationNote,
          authorizationReferringProvider,
        });
      }
    },
    callback: () => chartUploadBackgroundRefresh(),
  })
);

const triggerSmsMessage = ({
  sendToken,
  receiverType,
  patientName,
  messageText,
  patientPhoneNumber,
  resentSlug,
}) => (
  crud({
    dispatch: {
      begin: 'BEGIN_SMSMESSAGE',
      fail: 'FAILED_SMSMESSAGE',
      end: 'FINISH_SMSMESSAGE',
    },
    method: 'POST',
    url: `/api/v1/receivers/${sendToken}/sms_send_messages`,
    data: {
      receiver_type: receiverType,
      message_text: messageText,
      patient_name: patientName,
      patient_phone_number: patientPhoneNumber,
      resent_slug: resentSlug,
    },
    onSuccess: () => {
      if (process.env.NODE_ENV !== 'development' && posthog) {
        posthog.capture('trigger_sms_message', {
          sendToken,
          receiverType,
          patientName,
          messageText,
          patientPhoneNumber,
          resentSlug,
        });
      }
    },
  })
);

const triggerMairaCall = ({
  sendToken,
  receiverType,
  templateId,
  patientName,
  patientPhoneNumber,
  referringProvider,
}) => (
  crud({
    dispatch: {
      begin: 'BEGIN_MAIRA_CALL',
      fail: 'FAILED_MAIRA_CALL',
      end: 'FINISHED_MAIRA_CALL',
    },
    method: 'POST',
    url: `/api/v1/receivers/${sendToken}/outbound_calls`,
    data: {
      receiver_type: receiverType,
      template_id: templateId,
      patient_name: patientName,
      patient_phone_number: patientPhoneNumber,
      referring_provider: referringProvider,
    },
  })
);

const triggerTaskCreation = ({
  sendToken, receiverType, fileName, patientName = '', patientFirstName = '', patientLastName = '', patientMiddleName = '', patientDOB = '', patientMrn = '', assignTo = '', task = '', taskDetails = '', dueDate = '', priority = '', notes = '', associateDoc = false, facilityName = '',
}) => (
  crud({
    dispatch: {
      begin: 'BEGIN_TASK_CREATION',
      fail: 'FAILED_TASK_CREATION',
      end: 'FINISH_TASK_CREATION',
    },
    method: 'POST',
    url: `/api/v1/receivers/${sendToken}/emr_uploads`,
    data: {
      receiver_type: receiverType,
      ...(fileName === undefined ? {} : { file_name: fileName }),
      patient_name: patientName,
      patient_first_name: patientFirstName,
      patient_middle_name: patientMiddleName,
      patient_last_name: patientLastName,
      patient_dob: patientDOB,
      patient_mrn: patientMrn,
      practitioner_name: assignTo,
      assign_to: assignTo,
      task,
      task_details: taskDetails,
      due_date: dueDate,
      priority,
      notes,
      associate_doc: associateDoc,
      facility_name: facilityName,
    },
    callback: () => chartUploadBackgroundRefresh(),
    onSuccess: () => {
      if (process.env.NODE_ENV !== 'development' && posthog) {
        posthog.capture('trigger_task_creation', {
          sendToken,
          receiverType,
          patientName,
          patientFirstName,
          patientMiddleName,
          patientLastName,
          patientDOB,
          patientMrn,
          assignTo,
          task,
          taskDetails,
          dueDate,
          priority,
          notes,
          associateDoc,
          facilityName,
        });
      }
    },
  })
);

const triggerAutoUploadAndTaskCreation = ({
  sendToken, receiverType, documentCategory = '', fileName = '', patientName = '', patientFirstName = '', patientLastName = '', patientMiddleName = '', patientDOB = '', patientMrn = '', assignTo = '', task = '', taskDetails = '', dueDate = '', priority = '', notes = '', uploadAndCreateTask = true, facilityName = '', orderingProvider = '', dateOfService = '', fromReferralManagement = false,
  authorizationType = '', authorizationNumber = '', authorizationDescription = '', authorizationActiveStatus = false, authorizationUnlimitedVisits = false, authorizationNumberOfVisits = '', authorizationFromDate = '', authorizationToDate = '', authorizationNote = '', authorizationReferringProvider = '',
}) => (
  crud({
    dispatch: {
      begin: 'BEGIN_TASK_CREATION',
      fail: 'FAILED_TASK_CREATION',
      end: 'FINISH_TASK_CREATION',
    },
    method: 'POST',
    url: `/api/v1/receivers/${sendToken}/emr_uploads`,
    data: {
      receiver_type: receiverType,
      document_category: documentCategory,
      file_name: fileName,
      patient_name: patientName,
      patient_first_name: patientFirstName,
      patient_middle_name: patientMiddleName,
      patient_last_name: patientLastName,
      patient_dob: patientDOB,
      patient_mrn: patientMrn,
      practitioner_name: assignTo,
      task,
      task_details: taskDetails,
      due_date: dueDate,
      priority,
      notes,
      upload_and_create_task: uploadAndCreateTask,
      facility_name: facilityName,
      ordering_provider: orderingProvider,
      date_of_service: dateOfService,
      from_referral_management: fromReferralManagement,
      authorization_type: authorizationType,
      authorization_number: authorizationNumber,
      authorization_description: authorizationDescription,
      authorization_active_status: authorizationActiveStatus,
      authorization_unlimited_visits: authorizationUnlimitedVisits,
      authorization_number_of_visits: authorizationNumberOfVisits,
      authorization_from_date: authorizationFromDate,
      authorization_to_date: authorizationToDate,
      authorization_note: authorizationNote,
      authorization_referring_provider: authorizationReferringProvider,
    },
    callback: () => chartUploadBackgroundRefresh(),
  })
);

const createChartUpload = ({
  emrIntegrationCredentialId,
  data,
  uploadType,
  parentType,
  sendToken,
  idempotencyKey,
}) => (
  crud({
    dispatch: {
      begin: 'BEGIN_AUTOUPLOAD',
      fail: 'FAILED_AUTOUPLOAD',
      end: 'FINISH_AUTOUPLOAD',
    },
    method: 'POST',
    url: '/api/v1/chart_uploads',
    data: {
      emr_integration_credential_id: emrIntegrationCredentialId,
      upload_type: uploadType,
      data,
      parent_type: parentType,
      send_token: sendToken,
      idempotency_key: idempotencyKey,
    },
    // The drawer has no refresh route of its own, so fall forward to the foreground
    // refetch when background refresh is off (see chartUploadBackgroundRefresh).
    callback: () => chartUploadBackgroundRefresh({ foregroundFallback: true }),
  })
);

const setSelectedRecordDetails = (selectedRecordDetails) => {
  return {
    type: 'SET_SELECTED_RECORD_DETAILS',
    payload: {
      selectedRecordDetails,
      currentRecordId: selectedRecordDetails.file_token,
    },
  };
};

const setCurrentRecordId = (recordId) => {
  return {
    type: 'SET_CURRENT_RECORD_ID',
    payload: {
      recordId,
    },
  };
};

const clearCurrentRecordId = () => {
  return {
    type: 'CLEAR_CURRENT_RECORD_ID',
  };
};

const clearSelectedRecordDetails = () => {
  return {
    type: 'CLEAR_SELECTED_RECORD_DETAILS',
  };
};

const updateRecordInfo = (record) => {
  return {
    type: 'UPDATE_RECORD_INFO',
    payload: record,
  };
};

const fetchDocumentComments = (sendToken) => (
  crud({
    dispatch: {
      begin: 'BEGIN_FETCH_DOCUMENT_COMMENTS',
      fail: 'FAILED_FETCH_DOCUMENT_COMMENTS',
      end: 'FINISHED_FETCH_DOCUMENT_COMMENTS',
      // WHICH record was asked about. The response body carries no record identity,
      // so without this tag the reducer cannot tell a late answer for the record the
      // user just left from the answer for the one now open.
      meta: { sendToken },
    },
    method: 'GET',
    url: `/api/v1/document_comments/${sendToken}`,
  })
);

const createDocumentComment = (commentData) => (
  crud({
    dispatch: {
      begin: 'BEGIN_CREATE_DOCUMENT_COMMENT',
      fail: 'FAILED_CREATE_DOCUMENT_COMMENT',
      end: 'FINISHED_CREATE_DOCUMENT_COMMENT',
    },
    method: 'POST',
    url: '/api/v1/document_comments',
    data: commentData,
  })
);

const updateDocumentComment = (commentId, sendToken, notes) => (
  crud({
    dispatch: {
      begin: 'BEGIN_UPDATE_DOCUMENT_COMMENT',
      fail: 'FAILED_UPDATE_DOCUMENT_COMMENT',
      end: 'FINISHED_UPDATE_DOCUMENT_COMMENT',
    },
    method: 'PATCH',
    url: `/api/v1/document_comments/${commentId}`,
    data: {
      send_token: sendToken,
      notes: notes,
    },
  })
);

const deleteDocumentComment = (commentId, sendToken) => (
  crud({
    dispatch: {
      begin: 'BEGIN_DELETE_DOCUMENT_COMMENT',
      fail: 'FAILED_DELETE_DOCUMENT_COMMENT',
      end: 'FINISHED_DELETE_DOCUMENT_COMMENT',
    },
    method: 'DELETE',
    url: `/api/v1/document_comments/${commentId}`,
    data: {
      send_token: sendToken,
    },
  })
);

const clearDocumentComments = () => {
  return {
    type: 'CLEAR_DOCUMENT_COMMENTS',
  };
};

// Refresh the current records view after a send-to-chart / create-chart so the
// visible rows pick up backend-computed status (chart_in_progress,
// auto_upload_in_progress) without a manual refresh. Called by the chart modals
// (CreateNewChart / AutoUpload). With inbox_background_refresh on this rides the
// silent-refresh path; off, it keeps the legacy foreground refetch (loading
// spinner, history entry) — the current page AND filters live in the URL query,
// so refetching with window.location.search updates the rows in place.
const refreshCurrentRecords = () => (dispatch) => {
  if (inboxBackgroundRefreshEnabled()) return dispatch(chartUploadBackgroundRefresh());
  if (typeof window === 'undefined' || !window.location) return undefined;
  const path = window.location.pathname || '';
  const search = window.location.search || '';
  if (path.includes('/app/received')) return dispatch(getReceivedByQuery(search));
  if (path.includes('/app/assigned')) return dispatch(getAssignedByQuery(search));
  if (path.includes('/app/referred')) return dispatch(getReferralsQuery(search));
  if (path.includes('/app/uploads')) return dispatch(getUploadedByQuery(search));
  // ENG-3009: the Sent page can now start chart actions, so it needs the same
  // flag-off refetch branch as the other inboxes.
  if (path.includes('/app/sent')) return dispatch(getSentByQuery(search));
  return undefined;
};

export {
  refreshCurrentRecords,
  requestInboxTableRefresh,
  getUploadedRecordsByPage,
  getUploadedByQuery,
  getRecordById,
  getSentRecordBySendToken,
  getSentRecordsByPage,
  getSentByQuery,
  getStatusOfSentRecordById,
  getStatusOfSentPatientRecordById,
  printGuestRecord,
  printPatientRecord,
  printUserRecord,
  printSentRecord,
  getReceivedRecordsByPage,
  getReferralsByPage,
  getAssignedRecordsByPage,
  getAssignedRecord,
  getReceivedByQuery,
  getAssignedByQuery,
  getReferralsQuery,
  getReferralStatusCounts,
  buildReferralStatusCountsQuery,
  buildReferralStatusCountsUrl,
  archiveRecords,
  unarchiveRecords,
  archiveReceivedRecords,
  unarchiveReceivedRecords,
  uploadRecord,
  getReferralIngestionTargets,
  moveRecordToReferralManagement,
  runAiOnUploadedRecord,
  resendRecord,
  bulkResend,
  markDocumentAsCompleted,
  assignBackAndComplete,
  updatePatientInfo,
  updateEmailAttachmentPatientInfo,
  reportEmailAttachmentPatientUpdateError,
  updateSelectedRecordLabels,
  updateUploadedRecordPrivacy,
  editReceivedRecordTag,
  getRecordLink,
  getSecureSignedURL,
  revokeRecordAcessById,
  mergeRecords,
  resendAllFailedFaxes,
  downloadZip,
  printOrDownloadSecureLink,
  markRecordAsViewed,
  saveAnnotationsOnRecord,
  setUnsavedAnnotationsOnRecord,
  updateSignatures,
  submitForm,
  getSignature,
  getAttachments,
  saveAttachment,
  deleteAttachment,
  duplicateRecord,
  editReceivedRecordLabels,
  setRecordTeam,
  setRecordTeamBulk,
  splitUploadedRecord,
  splitReceivedRecord,
  triggerAutoUpload,
  triggerSmsMessage,
  triggerMairaCall,
  triggerSFTPUpload,
  triggerTaskCreation,
  triggerAutoUploadAndTaskCreation,
  createChartUpload,
  getNextRecord,
  updateAssignedRecord,
  updateReferral,
  assignDocument,
  getReferralDocumentStatus,
  mergeReferrals,
  getMergePreview,
  getReferralReplyUrl,
  getReferralReplyPreviewUrl,
  getReferralReplies,
  sendReferralReply,
  clearDownloadUrl,
  setSelectedRecordDetails,
  setCurrentRecordId,
  clearCurrentRecordId,
  clearSelectedRecordDetails,
  updateRecordInfo,
  updateReferralDueDate,
  fetchDocumentComments,
  createDocumentComment,
  updateDocumentComment,
  deleteDocumentComment,
  clearDocumentComments,
};
